import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface Location {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  email: string;
  profileImage: string;
  createdAt: Date;
}

export interface BusinessDetails {
  name: string;
  location: string;
  profileImage: string;
}

export interface Offering {
  id: string;
  name: string;
  description: string;
  image: string;
  offeringType: string;
  createdAt: Date;
}

export interface Redemption {
  id: string;
  offeringId: string;
  offeringName: string;
  userName: string;
  userEmail: string;
  redeemedAt: Date;
}

interface BusinessContextType {
  businessDetails: BusinessDetails | null;
  locations: Location[];
  offerings: Offering[];
  redemptions: Redemption[];
  updateBusinessDetails: (details: BusinessDetails) => void;
  addLocation: (location: Omit<Location, 'id' | 'createdAt'>) => void;
  updateLocation: (id: string, location: Omit<Location, 'id' | 'createdAt'>) => void;
  deleteLocation: (id: string) => void;
  addOffering: (offering: Omit<Offering, 'id' | 'createdAt'>) => void;
  deleteOffering: (id: string) => void;
}

const BusinessContext = createContext<BusinessContextType | undefined>(undefined);

// Mock data for redemptions
const mockRedemptions: Redemption[] = [
  {
    id: '1',
    offeringId: '1',
    offeringName: 'Free Coffee',
    userName: 'John Doe',
    userEmail: 'john@example.com',
    redeemedAt: new Date('2024-11-20T10:30:00')
  },
  {
    id: '2',
    offeringId: '1',
    offeringName: 'Free Coffee',
    userName: 'Jane Smith',
    userEmail: 'jane@example.com',
    redeemedAt: new Date('2024-11-21T14:15:00')
  },
  {
    id: '3',
    offeringId: '2',
    offeringName: '10% Discount',
    userName: 'Bob Johnson',
    userEmail: 'bob@example.com',
    redeemedAt: new Date('2024-11-22T09:00:00')
  }
];

// Mock data for locations
const mockLocations: Location[] = [
  {
    id: '1',
    name: 'Downtown Store',
    address: '123 Main Street',
    city: 'New York',
    state: 'NY',
    zipCode: '10001',
    phone: '(212) 555-0123',
    email: 'downtown@mybusiness.com',
    profileImage: '',
    createdAt: new Date('2024-10-15')
  },
  {
    id: '2',
    name: 'Uptown Location',
    address: '456 Park Avenue',
    city: 'New York',
    state: 'NY',
    zipCode: '10022',
    phone: '(212) 555-0456',
    email: 'uptown@mybusiness.com',
    profileImage: '',
    createdAt: new Date('2024-11-01')
  }
];

export const BusinessProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [businessDetails, setBusinessDetails] = useState<BusinessDetails | null>(null);
  const [locations, setLocations] = useState<Location[]>(mockLocations);
  const [offerings, setOfferings] = useState<Offering[]>([]);
  const [redemptions] = useState<Redemption[]>(mockRedemptions);

  const updateBusinessDetails = (details: BusinessDetails) => {
    setBusinessDetails(details);
  };

  const addLocation = (location: Omit<Location, 'id' | 'createdAt'>) => {
    const newLocation: Location = {
      ...location,
      id: Date.now().toString(),
      createdAt: new Date()
    };
    setLocations(prev => [...prev, newLocation]);
  };

  const updateLocation = (id: string, location: Omit<Location, 'id' | 'createdAt'>) => {
    setLocations(prev => prev.map(loc => 
      loc.id === id 
        ? { ...location, id, createdAt: loc.createdAt }
        : loc
    ));
  };

  const deleteLocation = (id: string) => {
    setLocations(prev => prev.filter(loc => loc.id !== id));
  };

  const addOffering = (offering: Omit<Offering, 'id' | 'createdAt'>) => {
    const newOffering: Offering = {
      ...offering,
      id: Date.now().toString(),
      createdAt: new Date()
    };
    setOfferings(prev => [...prev, newOffering]);
  };

  const deleteOffering = (id: string) => {
    setOfferings(prev => prev.filter(o => o.id !== id));
  };

  return (
    <BusinessContext.Provider
      value={{
        businessDetails,
        locations,
        offerings,
        redemptions,
        updateBusinessDetails,
        addLocation,
        updateLocation,
        deleteLocation,
        addOffering,
        deleteOffering
      }}
    >
      {children}
    </BusinessContext.Provider>
  );
};

export const useBusiness = () => {
  const context = useContext(BusinessContext);
  if (context === undefined) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
};

